import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Loader2 } from "lucide-react";

interface Vehicle {
  id: string;
  plate: string;
  make: string;
  model: string;
}

interface Driver {
  id: string;
  name: string;
}

interface CostCenter {
  id: string;
  name: string;
  code?: string;
}

interface Assignment {
  id: string;
  vehicle_id: string;
  driver_id: string;
  starts_at: string;
  ends_at: string | null;
  cost_center_id: string | null;
  status: string;
}

export interface AssignmentFormData {
  vehicle_id: string;
  driver_id: string;
  starts_at: string;
  ends_at: string;
  cost_center_id: string;
  status: string;
}

interface AssignmentFormSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  assignment?: Assignment | null;
  onSubmit: (data: AssignmentFormData) => void;
  isLoading?: boolean;
  vehicles?: Vehicle[];
  drivers?: Driver[];
  costCenters?: CostCenter[];
}

export function AssignmentFormSheet({
  open,
  onOpenChange,
  assignment,
  onSubmit,
  isLoading = false,
  vehicles = [],
  drivers = [],
  costCenters = [],
}: AssignmentFormSheetProps) {
  const [formData, setFormData] = useState<AssignmentFormData>({
    vehicle_id: "",
    driver_id: "",
    starts_at: "",
    ends_at: "",
    cost_center_id: "",
    status: "active",
  });

  useEffect(() => {
    if (assignment) {
      setFormData({
        vehicle_id: assignment.vehicle_id,
        driver_id: assignment.driver_id,
        starts_at: assignment.starts_at,
        ends_at: assignment.ends_at || "",
        cost_center_id: assignment.cost_center_id || "",
        status: assignment.status,
      });
    } else {
      setFormData({
        vehicle_id: "",
        driver_id: "",
        starts_at: "",
        ends_at: "",
        cost_center_id: "",
        status: "active",
      });
    }
  }, [assignment, open]);

  const handleSubmit = (e?: React.FormEvent) => {
    e?.preventDefault();
    onSubmit(formData);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:w-[450px] p-0">
        <SheetHeader className="px-6 pt-6 pb-4 border-b">
          <SheetTitle>
            {assignment ? "Editar Alocação" : "Nova Alocação"}
          </SheetTitle>
          <SheetDescription>
            {assignment
              ? "Edite os dados da alocação selecionada."
              : "Preencha os dados para criar uma nova alocação."}
          </SheetDescription>
        </SheetHeader>

        <ScrollArea className="h-[calc(100vh-180px)] px-6 py-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="vehicle_id">Veículo</Label>
              <Select
                value={formData.vehicle_id}
                onValueChange={(value) =>
                  setFormData({ ...formData, vehicle_id: value })
                }
                required
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione um veículo" />
                </SelectTrigger>
                <SelectContent>
                  {vehicles?.map((vehicle) => (
                    <SelectItem key={vehicle.id} value={vehicle.id}>
                      {vehicle.plate} - {vehicle.make} {vehicle.model}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="driver_id">Motorista</Label>
              <Select
                value={formData.driver_id}
                onValueChange={(value) =>
                  setFormData({ ...formData, driver_id: value })
                }
                required
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione um motorista" />
                </SelectTrigger>
                <SelectContent>
                  {drivers?.map((driver) => (
                    <SelectItem key={driver.id} value={driver.id}>
                      {driver.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="cost_center_id">Centro de Custo (Opcional)</Label>
              <Select
                value={formData.cost_center_id}
                onValueChange={(value) =>
                  setFormData((prev) => ({ ...prev, cost_center_id: value }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione um centro de custo" />
                </SelectTrigger>
                <SelectContent className="bg-background border z-50">
                  <SelectItem value="none">Nenhum</SelectItem>
                  {costCenters?.map((costCenter) => (
                    <SelectItem key={costCenter.id} value={costCenter.id}>
                      {costCenter.name}
                      {costCenter.code ? ` (${costCenter.code})` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="starts_at">Data de Início</Label>
              <Input
                id="starts_at"
                type="date"
                value={formData.starts_at}
                onChange={(e) =>
                  setFormData({ ...formData, starts_at: e.target.value })
                }
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="ends_at">Data de Término (Opcional)</Label>
              <Input
                id="ends_at"
                type="date"
                value={formData.ends_at}
                onChange={(e) =>
                  setFormData({ ...formData, ends_at: e.target.value })
                }
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <Select
                value={formData.status}
                onValueChange={(value) =>
                  setFormData({ ...formData, status: value })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Ativo</SelectItem>
                  <SelectItem value="inactive">Inativo</SelectItem>
                  <SelectItem value="completed">Concluído</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </form>
        </ScrollArea>

        <SheetFooter className="px-6 py-4 border-t">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
          >
            Cancelar
          </Button>
          <Button type="submit" onClick={handleSubmit} disabled={isLoading}>
            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {assignment ? "Atualizar" : "Criar"}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
